import { z } from "zod";
import { stamps, Money, Uuid } from "./common.js";
import { canonicalType, confidence, productDomain, reviewState } from "./enums.js";

/**
 * A product's membership in a comparable group, with a summary of the product
 * for the review screen.
 */
export const GroupMemberDto = z.object({
  id: Uuid,
  groupId: Uuid,
  productId: Uuid,
  /** Pair score from the ported scorer (type 45 + size 35/12/5 + 0.2 x token_set_ratio; min 55). */
  score: z.number(),
  confidence: confidence,
  needsReview: z.boolean(),
  reviewState: reviewState,
  /** LLM rationale when AI assisted the grouping. */
  rationale: z.string().nullable(),
  // Product summary fields
  description: z.string(),
  brandId: Uuid.nullable(),
  brandName: z.string().nullable(),
  iqCode: z.string().nullable(),
  manPartNumber: z.string().nullable(),
  packQty: z.number().int().nullable(),
  uom: z.string().nullable(),
  ...stamps,
});
export type GroupMember = z.infer<typeof GroupMemberDto>;

/** Pre-computed lowest current cost per supplier per group (refreshed on publish). */
export const SupplierLowDto = z.object({
  id: Uuid,
  groupId: Uuid,
  supplierId: Uuid,
  productId: Uuid,
  /** Exact current price row the low points at. */
  priceId: Uuid,
  nettPrice: Money,
  ...stamps,
});
export type SupplierLow = z.infer<typeof SupplierLowDto>;

/** Cross-brand comparable group (n-ary; e.g. "15mm push-fit elbow"). */
export const ComparableGroupDto = z.object({
  id: Uuid,
  domain: productDomain,
  canonicalType: canonicalType,
  sizeSignature: z.string(),
  label: z.string(),
  members: z.array(GroupMemberDto),
  supplierLows: z.array(SupplierLowDto),
  ...stamps,
});
export type ComparableGroup = z.infer<typeof ComparableGroupDto>;

/** `PATCH /api/comparables/members/:id` body. */
export const MemberReviewInput = z.object({
  reviewState: reviewState.extract(["approved", "rejected"]),
});
export type MemberReviewInput = z.infer<typeof MemberReviewInput>;
